import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import classSubjectService from "../../services/classSubjectService";
import { queryKeys } from "./queryKeys";

// ==================== QUERY HOOKS ====================

export const useClassSubjects = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.classSubject.all, filters],
    queryFn: () => classSubjectService.getClassSubjects(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useClassSubjectById = (id) => {
  return useQuery({
    queryKey: queryKeys.classSubject.detail(id),
    queryFn: () => classSubjectService.getClassSubjectById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSubjectsByClass = (classId, params = {}) => {
  const normalizedClassId =
    classId != null && classId !== "" ? String(classId) : null;
  const academicYear =
    params?.academicYear != null && String(params.academicYear).trim() !== ""
      ? String(params.academicYear).trim()
      : null;
  const requestParams = academicYear ? { academicYear } : {};

  return useQuery({
    queryKey: [
      ...queryKeys.classSubject.byClass(normalizedClassId),
      { academicYear },
    ],
    queryFn: () =>
      classSubjectService.getSubjectsByClass(normalizedClassId, requestParams),
    enabled: !!normalizedClassId,
    // Cascading dropdowns (timetable slot / wizard): always refetch when class or year changes
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
};

export const useClassesBySubject = (subjectId) => {
  return useQuery({
    queryKey: queryKeys.classSubject.bySubject(subjectId),
    queryFn: () => classSubjectService.getClassesBySubject(subjectId),
    enabled: !!subjectId,
    staleTime: 5 * 60 * 1000,
  });
};

// ==================== MUTATION HOOKS ====================

export const useAssignSubjectToClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => classSubjectService.assignSubjectToClass(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.classSubject.all,
      });
    },
  });
};

export const useUpdateClassSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => classSubjectService.updateClassSubject(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.classSubject.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.classSubject.detail(variables.id),
      });
    },
  });
};

export const useDeleteClassSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => classSubjectService.deleteClassSubject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.classSubject.all,
      });
    },
  });
};
